import { HttpClient, HttpErrorResponse } from '@angular/common/http';
import { Injectable } from '@angular/core';
import { Observable, catchError, throwError } from 'rxjs';
import { environment } from '../../environments/environment';

@Injectable({
  providedIn: 'root'
})
export class EnrollmentService {

  private _baseUrl: string = "";
  private _controllerName: string = "";

  constructor(private _http: HttpClient) {
    this._baseUrl = environment.apiUrl;
    this._controllerName = "Enrollment"
  }

  // private getHeaders(): HttpHeaders{
  //   return new HttpHeaders({
  //     'Content-Type': 'application/json',
  //     'Authorization': `Bearer ${this.getToken()}`
  //   })
  // }

  insert(payload: any): Observable<any> {
    return this._http.post<any>(`${this._baseUrl}/${this._controllerName}`, payload).pipe(catchError(this.handleErrors))
  }

  update(id: number, payload: any): Observable<any> {
    return this._http.put<any>(`${this._baseUrl}/${this._controllerName}/${id}`, payload).pipe(catchError(this.handleErrors))
  }

  appendObservation(id: number, note: string): Observable<any> {
    return this._http.patch<any>(
      `${this._baseUrl}/${this._controllerName}/${id}/observations/append`,
      { note }
    ).pipe(catchError(this.handleErrors));
  }

  /** 🆕 Contexto para el modal de edición de valor de matrícula (referencia del proyecto). */
  getInscriptionContext(id: number): Observable<any> {
    return this._http.get<any>(
      `${this._baseUrl}/${this._controllerName}/${id}/inscription-context`
    ).pipe(catchError(this.handleErrors));
  }

  /** 🆕 Actualiza el valor de matrícula y recalcula el descuento en backend. */
  updateInscriptionAmount(id: number, newTotalAmount: number, comment: string): Observable<any> {
    return this._http.patch<any>(
      `${this._baseUrl}/${this._controllerName}/${id}/inscription-amount`,
      { newTotalAmount, comment }
    ).pipe(catchError(this.handleErrors));
  }

  delete(id: number): Observable<any> {
    return this._http.delete<any>(`${this._baseUrl}/${this._controllerName}/${id}`).pipe(catchError(this.handleErrors))
  }

  getAll(): Observable<any> {
    return this._http.get<any>(`${this._baseUrl}/${this._controllerName}/`).pipe(catchError(this.handleErrors))
  }

  getIndividuals(params?: any): Observable<any> {
    return this._http.get<any>(`${this._baseUrl}/${this._controllerName}/individuals`, { params: params }).pipe(catchError(this.handleErrors));
  }

  getStats(type: 'individual' | 'corporate'): Observable<any> {
    return this._http.get<any>(`${this._baseUrl}/${this._controllerName}/stats`, { params: { type } }).pipe(catchError(this.handleErrors));
  }

  getCorporate(params?: any): Observable<any> {
    return this._http.get<any>(`${this._baseUrl}/${this._controllerName}/corporate`, { params: params }).pipe(catchError(this.handleErrors));
  }

  getCorporateDetails(agreementId: number): Observable<any> {
    return this._http.get<any>(`${this._baseUrl}/CorporateEnrollment/${agreementId}/details`).pipe(catchError(this.handleErrors));
  }

  getById(id: number): Observable<any> {
    return this._http.get<any>(`${this._baseUrl}/${this._controllerName}/${id}`).pipe(catchError(this.handleErrors))
  }

  getEnrollmentsByStudentId(id: number | undefined): Observable<any> {
    return this._http.get<any>(`${this._baseUrl}/${this._controllerName}/student/${id}`).pipe(catchError(this.handleErrors))
  }

  getPendingPayments(id: number | undefined): Observable<any> {
    return this._http.get<any>(`${this._baseUrl}/${this._controllerName}/${id}/pending-payments`).pipe(catchError(this.handleErrors))
  }

  /**
   * Obtiene los ingresos generales (sin plan de pago) de una inscripción
   * Estos son pagos por servicios adicionales, certificados, etc.
   */
  getGeneralIncomeReceipts(id: number): Observable<any> {
    return this._http.get<any>(`${this._baseUrl}/${this._controllerName}/${id}/general-income-receipts`).pipe(catchError(this.handleErrors))
  }

  /**
   * Obtiene los ingresos generales (sin plan de pago) asociados a un acuerdo corporativo.
   * Incluye pagos de la empresa y de los participantes del acuerdo no vinculados a un plan.
   */
  getCorporateGeneralIncomeReceipts(agreementId: number): Observable<any> {
    return this._http.get<any>(`${this._baseUrl}/CorporateEnrollment/${agreementId}/general-income-receipts`).pipe(catchError(this.handleErrors))
  }

  refinance(id: number | undefined, payload: any): Observable<any> {
    return this._http.post<any>(`${this._baseUrl}/${this._controllerName}/${id}/refinance`, payload).pipe(catchError(this.handleErrors))
  }

  /**
   * Actualiza el estado de una inscripción individual
   */
  updateStatus(id: number, status: number, reason?: string): Observable<any> {
    const payload: any = { status };
    if (reason) {
      payload.withdrawalReason = reason;
    }
    return this._http.patch<any>(`${this._baseUrl}/${this._controllerName}/${id}/status`, payload).pipe(catchError(this.handleErrors));
  }

  /**
   * Actualiza el estado de una inscripción corporativa
   */
  updateCorporateStatus(agreementId: number, status: number, reason?: string): Observable<any> {
    const payload: any = { status };
    if (reason) {
      payload.withdrawalReason = reason;
    }
    return this._http.patch<any>(`${this._baseUrl}/CorporateEnrollment/${agreementId}/status`, payload).pipe(catchError(this.handleErrors));
  }

  /**
   * 🆕 Actualiza el flag de cobro automático de una inscripción individual
   */
  updateAutoPayment(id: number, enabled: boolean): Observable<any> {
    return this._http.patch<any>(`${this._baseUrl}/${this._controllerName}/${id}/auto-payment`, enabled).pipe(catchError(this.handleErrors));
  }

  /**
   * 🆕 Actualiza el flag de cobro automático de una inscripción corporativa
   */
  updateCorporateAutoPayment(agreementId: number, enabled: boolean): Observable<any> {
    return this._http.patch<any>(`${this._baseUrl}/CorporateEnrollment/${agreementId}/auto-payment`, enabled).pipe(catchError(this.handleErrors));
  }

  /**
   * 🆕 Anula una inscripción individual
   */
  cancel(id: number, reason: string): Observable<any> {
    return this._http.put<any>(`${this._baseUrl}/${this._controllerName}/${id}/cancel`, { cancellationReason: reason }).pipe(catchError(this.handleErrors));
  }

  /**
   * Corrige inconsistencias en estados de pago de cuotas
   */
  fixInconsistencies(): Observable<any> {
    return this._http.post<any>(`${this._baseUrl}/${this._controllerName}/fix-inconsistencies`, {}).pipe(catchError(this.handleErrors));
  }

  /**
   * Descarga el reporte Excel (.xlsx) con el detalle de una inscripción individual:
   * ficha, KPIs, plan de pagos y comprobantes de ingreso.
   */
  exportExcel(id: number): Observable<Blob> {
    return this._http.get(`${this._baseUrl}/${this._controllerName}/${id}/excel`, {
      responseType: 'blob'
    }).pipe(catchError(this.handleErrors));
  }


  private handleErrors(error: HttpErrorResponse) {
    let errorMessage = 'Algo salió mal; por favor, intente nuevamente más tarde.';

    if (error.error instanceof ErrorEvent) {
      // Error del lado del cliente
      console.log("Ocurrió un error del cliente:", error.error.message);
      errorMessage = error.error.message;
    } else {
      // Error del lado del servidor
      console.log(`Error del servidor ${error.status}:`, error.error);

      // Extraer mensaje de error del backend (puede ser string o objeto)
      if (error.error) {
        if (typeof error.error === 'string') {
          errorMessage = error.error;
        } else if (error.error.message) {
          errorMessage = error.error.message;
        } else if (error.error.errorMessage) {
          errorMessage = error.error.errorMessage;
        } else if (error.error.title) {
          errorMessage = error.error.title;
        }
      }
    }

    return throwError(() => ({ message: errorMessage, status: error.status, originalError: error.error }));
  }
}
